// Dependencies
import { call, put } from "redux-saga/effects";

// Services
import api from "../../services/api";

// Types
import HttpStatus from "enums/HttpStatus";
import { AxiosResponse } from "axios";
import { IContentPerType } from "interfaces/IContentPerType";
import { ILessonPlanEvent } from "interfaces/ILessonPlan";

// Stores
import { Creators as alertActions } from "../ducks/alert";
import { Creators as watchActions, IWatchVirtualAssistantMessage, IWatchVirtualAssistantMessageRole } from "../ducks/watch";

export function* getWatchBookletModule(action: any) {
    const { moduleId } = action.payload;
    try {
        if (!moduleId) {
            return;
        }

        const { data } = yield call(api.get, `/bookletmodule/${moduleId}`, {
            headers: {
                "X-Relations": "content.learningObject.video;subject;events;progress;subjectFront"
            }
        });

        yield put(watchActions.getWatchBookletModuleSuccess(data));
    } catch (error) {
        yield put(watchActions.getWatchBookletModuleFailure(error?.response?.data));
        yield put(alertActions.showAlert("Módulo não encontrado!", "danger"));
    }
}

export function* getWatchVideo(action: any) {
    const { id } = action.payload;

    try {
        const { data } = yield call(api.get, `/video/${id}`);

        // Formata a playlist pro padrao de video relacionado
        const formatData = {
            ...data,
            ...(data.playlist && {
                playlist: data.playlist.map((item: any) => ({
                    ...item.video,
                    viewed: item.viewed,
                    isLiked: item.isLiked
                }))
            })
        };

        yield put(watchActions.getWatchVideoSuccess(formatData));

        const { teachers } = data;

        if (!!teachers && !!teachers.length) {
            yield put(watchActions.setWatch({ teachers }));
        }
    } catch (error) {
        if (error.response && error.response.status !== HttpStatus.PAYMENT_REQUIRED) {
            yield put(alertActions.showAlert("Vídeo não encontrado!", "danger"));
        }

        yield put(watchActions.getWatchVideoFailure(error?.response?.data));
    }
}

export function* setWatchLike(action: any) {
    const { id, status } = action.payload;
    try {
        yield call(api.patch, `/learningobject/${id}/like`, {
            isLike: status
        });

        yield put(watchActions.setWatchLikeSuccess({ id, status }));
    } catch (error) {
        yield put(watchActions.setWatchLikeFailure(error?.response?.data));
    }
}

export function* getWatchLessonPlan(action: any) {
    try {
        const { eventId } = action.payload;

        if (!eventId) {
            return;
        }

        const { data }: AxiosResponse<ILessonPlanEvent> = yield call(api.get, `/lessonplanevent/${eventId}`, {
            headers: {
                "X-Relations": "teachers;bookletModules;module"
            }
        });

        const { video, teachers, contentPerType, category, subjectObj, startDate, title, endDate } = data;

        // Verifica se existe conteudo relacionado no contentPerType
        const relatedVideos = contentPerType.find((item: IContentPerType) => item.type === "Video");
        const relatedExerciseList = contentPerType.find((item: IContentPerType) => item.type === "ExerciseList");
        const relatedMaterial = contentPerType.find((item: IContentPerType) => item.type === "Material");
        const relatedBooklet = contentPerType.find((item: IContentPerType) => item.type === "Apostila");

        const updateContent = {
            ...(!!startDate && { eventStartDate: startDate }),
            ...(!!endDate && { eventEndDate: endDate }),
            ...(!!teachers && !!teachers.length && { teachers }),
            ...(!!relatedVideos && !!relatedVideos.items.length && { relatedVideos: relatedVideos.items }),
            ...(!!relatedExerciseList && !!relatedExerciseList.items.length && { exerciseLists: relatedExerciseList.items }),
            ...(!!relatedMaterial && !!relatedMaterial.items.length && { materials: relatedMaterial.items }),
            ...(!!relatedBooklet && !!relatedBooklet.items.length && { booklets: relatedBooklet.items }),
            ...((!!category || category === 0) && { category }),
            ...(!!data.module && { module: data.module }),
            ...(!!video && { video }),
            ...(!!subjectObj && { subjectObj }),
            hasForum: data.hasForum || false,
            title
        };

        yield put(watchActions.setWatch(updateContent));

        yield put(watchActions.getWatchLessonPlanSuccess(data));
    } catch (error) {
        yield put(alertActions.showAlert("Evento não encontrado!", "danger"));
        yield put(watchActions.getWatchLessonPlanFailure(error?.response?.data));
    }
}

export function* getVirtualAssistantAnswers(action: any) {
    try {
        const { moduleId, objectId } = action.payload;

        const { data } = yield call(api.get, `/virtual-assistant`, {
            params: {
                module: moduleId,
                video: objectId
            }
        });

        const formattedMessages = (data?.messages ?? []).map(
            (message: any): IWatchVirtualAssistantMessage => {
                const { id, role, content } = message;
                const suggestionsMatches = [
                    "Perguntas para Reflexão",
                    "Novas perguntas para você considerar",
                    "Novas perguntas",
                    "Aqui estão algumas perguntas para você considerar",
                    "Enquanto isso, aqui estão algumas perguntas que você pode considerar",
                    "Enquanto isso, aqui estão algumas sugestões de tópicos que você pode considerar"
                ];
                const hasStringInAnswer = suggestionsMatches.some((match) => content.includes(match));
                let formattedContent = content;

                if (!!hasStringInAnswer) {
                    const stringInAnswer = suggestionsMatches.find((match) => content.includes(match));
                    const stringIndex = content.indexOf(stringInAnswer);
                    formattedContent = content.slice(0, stringIndex);
                }

                return {
                    id,
                    role,
                    content: formattedContent
                };
            }
        );

        yield put(watchActions.getVirtualAssistantAnswersSuccess(formattedMessages));
    } catch (error) {
        yield put(watchActions.getVirtualAssistantAnswersFailure(error.message));
    }
}

export function* sendVirtualAssistantQuestion(action: any) {
    try {
        const { messages, moduleId, objectId, question } = action.payload;

        const formattedMessageBeforeSendQuestion: IWatchVirtualAssistantMessage[] = [
            ...(messages ?? []),
            {
                content: question,
                role: IWatchVirtualAssistantMessageRole["Usuario"]
            },
            {
                content: "Pensando...",
                role: IWatchVirtualAssistantMessageRole["Assistente"]
            }
        ];

        yield put(
            watchActions.sendVirtualAssistantQuestionSuccess({
                items: formattedMessageBeforeSendQuestion,
                isSending: true
            })
        );

        window.dispatchEvent(new CustomEvent("virtual-assistant-should-abort-typing-animation"));

        const { data } = yield call(api.post, `/virtual-assistant`, {
            moduleId,
            objectId,
            message: question
        });

        let formattedAnswer = data
            .replace(/\n\n/g, "")
            .split("data: ")
            .join("");

        const suggestionsMatches = [
            "Perguntas para Reflexão",
            "Novas perguntas para você considerar",
            "Novas perguntas",
            "Aqui estão algumas perguntas para você considerar",
            "Enquanto isso, aqui estão algumas perguntas que você pode considerar",
            "Enquanto isso, aqui estão algumas sugestões de tópicos que você pode considerar"
        ];
        const hasStringInAnswer = suggestionsMatches.some((match) => formattedAnswer.includes(match));
        let suggestions;

        if (!!hasStringInAnswer) {
            const stringInAnswer = suggestionsMatches.find((match) => formattedAnswer.includes(match));
            const stringIndex = formattedAnswer.indexOf(stringInAnswer);
            const stringLength = stringInAnswer?.length;
            const stringIndexEnd = stringIndex + stringLength;
            const suggestionsString = formattedAnswer.slice(stringIndexEnd);
            const suggestionsArray = suggestionsString.split("\n");
            suggestions = suggestionsArray.map((suggestion: any) => suggestion.replace(/[*]/g, "").trim()).slice(1);
            formattedAnswer = formattedAnswer.slice(0, stringIndex);
        }

        const formattedMessagesAfterSendQuestion: IWatchVirtualAssistantMessage[] = [
            ...formattedMessageBeforeSendQuestion.slice(0, -1),
            {
                content: formattedAnswer.trim(),
                role: IWatchVirtualAssistantMessageRole["Assistente"]
            }
        ];

        yield put(
            watchActions.sendVirtualAssistantQuestionSuccess({
                items: formattedMessagesAfterSendQuestion,
                suggestions,
                isSending: false
            })
        );
    } catch (error) {
        yield put(
            watchActions.sendVirtualAssistantQuestionFailure(
                "Poxa! Eu peço desculpas mas, não consegui encontrar uma resposta para sua pergunta. Tente reformulá-la por favor."
            )
        );
    }
}
